import { RolodexEntryQueryBody, useRolodexEntries } from 'features/rolodex'
import isEmpty from 'lodash/isEmpty'
import { useMemo } from 'react'
import { GeckoEntry, RolodexEntryTagsType, RolodexEntryType } from 'types'
import z from 'zod'

export const LIBRA_DASHBOARD_CONFIG_ENTRY_TYPE =
  'libra-dashboard-config' as RolodexEntryType

const libraDashboardConfigSchema = z
  .object({
    title: z.string().readonly().default('Untitled Dashboard'),
  })
  .readonly()

export type LibraDashboardConfig = z.infer<
  typeof libraDashboardConfigSchema
> & {
  id: string
  tags: RolodexEntryTagsType
}

const mapEntryToLibraDashboardConfig = (
  entry: GeckoEntry
): LibraDashboardConfig => {
  const parsed = libraDashboardConfigSchema.parse(entry.data)
  return {
    id: entry.id,
    tags: entry.tags,
    ...parsed,
  }
}

export function useDashboardConfigs({
  tags,
}: {
  tags?: RolodexEntryTagsType
}): {
  configs: ReadonlyArray<LibraDashboardConfig>
  isLoading: boolean
  hasEverLoaded: boolean
  forceRefresh: () => void
} {
  const queryBody = useMemo<RolodexEntryQueryBody | undefined>(() => {
    const hasTags = tags && !isEmpty(tags)
    if (!hasTags) {
      return undefined
    }

    return {
      type: LIBRA_DASHBOARD_CONFIG_ENTRY_TYPE,
      tags,
    }
  }, [tags])

  const {
    entries: dashboardConfigs,
    isLoading,
    hasEverLoaded,
    forceRefresh,
  } = useRolodexEntries(queryBody)

  return useMemo(
    () => ({
      configs: (dashboardConfigs ?? []).map(mapEntryToLibraDashboardConfig),
      isLoading,
      hasEverLoaded,
      forceRefresh,
    }),
    [dashboardConfigs, forceRefresh, hasEverLoaded, isLoading]
  )
}
